/**
 * Section-card chrome for the GRN composer — a faithful Tailwind
 * translation of the prototype's `.section` / `.section-head` /
 * `.section-body` rules (shared shape with the PO composer):
 *
 *   - `.section`      → rounded border + paper bg + subtle shadow, overflow hidden
 *   - `.section-head` → 14px/18px top, 10px/18px bottom, 1px border-b
 *   - `.step` badge   → 20×20 rounded-full, mono numerals, bg-bg / text-muted
 *   - `.section-head h3`  → 13px / 600
 *   - `.section-head .sub`→ 12px / muted, indented under the title
 *   - `.section-body` → 16px/18px padding
 *
 * The `GrnField` / `GrnFieldGrid` helpers translate `.field`, `.field label`,
 * `.field-help`, `.req` and `.field-grid cols-2/cols-3` from the same sheet.
 *
 * @see prototype/purchases/goods-receipt-new.html — `.section` / `.field` CSS
 */
import type { ReactNode } from "react";

interface GrnSectionCardProps {
  step?: string | undefined;
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
  /** When true the body padding is dropped — used by the received-qty table
   *  whose `.items-table-wrap` spans the card edge-to-edge. */
  bodyless?: boolean | undefined;
  children: ReactNode;
}

export function GrnSectionCard({
  step,
  title,
  description,
  action,
  bodyless = false,
  children,
}: GrnSectionCardProps) {
  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex items-start justify-between gap-3 border-b border-line px-[18px] pb-2.5 pt-3.5">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            {step ? (
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-bg font-mono text-xs font-bold text-muted">
                {step}
              </span>
            ) : null}
            {title}
          </h3>
          {description ? <p className="ml-7 mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-1.5">{action}</div> : null}
      </header>
      {bodyless ? children : <div className="px-[18px] py-4">{children}</div>}
    </section>
  );
}

interface GrnFieldProps {
  id?: string | undefined;
  label: string;
  required?: boolean | undefined;
  help?: ReactNode | undefined;
  error?: string | undefined;
  children: ReactNode;
}

/** Translates the prototype's `.field` block (label · control · help). */
export function GrnField({ id, label, required = false, help, error, children }: GrnFieldProps) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-xs font-semibold text-ink-2">
        {label}
        {required ? <span className="ml-0.5 text-rose-600">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="text-xs font-medium text-rose-600">{error}</p>
      ) : help ? (
        <p className="text-xs leading-snug text-muted">{help}</p>
      ) : null}
    </div>
  );
}

/** Translates `.field-grid cols-2 / cols-3` — a 12px-gap responsive grid. */
export function GrnFieldGrid({
  cols,
  className,
  children,
}: {
  cols: 2 | 3;
  className?: string | undefined;
  children: ReactNode;
}) {
  const colClass = cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  return (
    <div className={`grid grid-cols-1 gap-3 ${colClass} ${className ?? ""}`}>{children}</div>
  );
}
